import { HttpClient, HttpParams } from '@angular/common/http';
import { toHttpParams } from './http-params';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type CaseStatus = 'mottatt' | 'under-behandling' | 'lost' | 'avvist';
export type CaseThread = 'external' | 'internal';

export interface CaseMessageView {
  id: string;
  thread: CaseThread;
  authorUserId: string;
  authorRole: string;
  /** Display name: the external thread shows board replies as "Styret"; the internal thread the individual. */
  authorName: string;
  body: string;
  createdAtUtc: string;
}

export interface FileCaseInput {
  category: string;
  title: string;
  description: string;
  attachments?: string[];
  location?: string;
  isAnonymous?: boolean;
  linkedResourceId?: string;
}

export interface CaseReceipt {
  id: string;
  caseNumber: number;
  expectedResponseHours: number;
  status: CaseStatus;
}

export interface ResidentCaseSummary {
  id: string;
  caseNumber: number;
  category: string;
  title: string;
  status: CaseStatus;
  expectedResponseHours: number;
  createdAtUtc: string;
  updatedAtUtc: string;
}

/**
 * The resident's view of a case. Note there is **no** internal-thread field — the
 * internal board thread is structurally absent from the resident contract (the boundary is
 * enforced server-side AND in this type), so no client code path could ever render it.
 */
export interface ResidentCaseDetail {
  id: string;
  caseNumber: number;
  category: string;
  title: string;
  description: string;
  status: CaseStatus;
  expectedResponseHours: number;
  attachments: string[];
  externalThread: CaseMessageView[];
  createdAtUtc: string;
}

export interface BoardCaseSummary {
  id: string;
  caseNumber: number;
  category: string;
  title: string;
  status: CaseStatus;
  assigneeUserId?: string | null;
  reporterUserId?: string | null;
  isAnonymous: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  /** Reporter display name, or null when anonymous. */
  reporterName?: string | null;
  assigneeName?: string | null;
}

export interface BoardCaseDetail {
  id: string;
  caseNumber: number;
  category: string;
  title: string;
  description: string;
  status: CaseStatus;
  assigneeUserId?: string | null;
  reporterUserId?: string | null;
  isAnonymous: boolean;
  deadline?: string | null;
  expectedResponseHours: number;
  attachments: string[];
  externalThread: CaseMessageView[];
  internalThread: CaseMessageView[];
  derivedTaskId?: string | null;
  derivedPostId?: string | null;
  createdAtUtc: string;
  reporterName?: string | null;
  reporterUnitLabel?: string | null;
  assigneeName?: string | null;
}

export interface BoardCaseFilter {
  status?: CaseStatus;
  category?: string;
  assignee?: string;
}

export interface UpdateCaseInput {
  assigneeUserId?: string;
  status?: CaseStatus;
  deadline?: string;
}

export interface ConvertToOppslagInput {
  category: string;
  title: string;
  body: string;
}

/**
 * Saker client (`/api/saker` resident + `/api/styre/saker` board). Hand-written to mirror the
 * Phase M6 backend contract. The resident and board surfaces are separate methods returning
 * separate types — the resident type cannot carry internal-thread data.
 */
@Injectable({ providedIn: 'root' })
export class SakerService {
  private readonly http = inject(HttpClient);

  // ── Resident ──
  file(input: FileCaseInput): Observable<CaseReceipt> {
    return this.http.post<CaseReceipt>('/api/saker', input);
  }

  get(id: string): Observable<ResidentCaseDetail> {
    return this.http.get<ResidentCaseDetail>(`/api/saker/${encodeURIComponent(id)}`);
  }

  reply(id: string, body: string): Observable<void> {
    return this.http.post<void>(`/api/saker/${encodeURIComponent(id)}/meldinger`, { body });
  }

  // ── Board (styre) ──
  boardList(filter: BoardCaseFilter = {}): Observable<BoardCaseSummary[]> {
    const params = toHttpParams(filter);
    return this.http.get<BoardCaseSummary[]>('/api/styre/saker', { params });
  }

  boardCreate(input: FileCaseInput): Observable<CaseReceipt> {
    return this.http.post<CaseReceipt>('/api/styre/saker', input);
  }

  boardUpdate(id: string, patch: UpdateCaseInput): Observable<void> {
    return this.http.put<void>(`/api/styre/saker/${encodeURIComponent(id)}`, patch);
  }

  boardReply(id: string, thread: CaseThread, body: string): Observable<void> {
    const params = new HttpParams().set('thread', thread);
    return this.http.post<void>(`/api/styre/saker/${encodeURIComponent(id)}/meldinger`, { body }, { params });
  }

  convertToTask(id: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`/api/styre/saker/${encodeURIComponent(id)}/gjor-om-til-oppgave`, {});
  }

  convertToOppslag(id: string, input: ConvertToOppslagInput): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`/api/styre/saker/${encodeURIComponent(id)}/gjor-om-til-oppslag`, input);
  }
}
